import { Button } from "react-bootstrap";
import clipboardSurveysIcon from "@/assets/clipboardSurveys.svg";
import { useUserPermissions } from "../../utils/auth";
import { SectionPermissionTypeEnum } from "../../types/SectionPermissionTypesEnum";
import { isActiveState, isInactiveState } from "../../utils/activeStateUtils";

interface BaseListFields {
    id: number;
    endDate?: Date | string;
    state?: string;
}

interface AppTableRowActionsProps<T extends BaseListFields> {
    object: T;
    sectionPermission?: SectionPermissionTypeEnum;
    onDelete?: (object: T) => void;
    canDelete?: (object: T) => boolean;
    deleteCondition?: (object: T) => boolean;
    onEnd?: (object: T) => void;
    canEnd?: (object: T) => boolean;
    onView?: (id: number) => void;
    canView?: (object: T) => boolean;
    onRestore?: (object: T) => void;
    canRestore?: (object: T) => boolean;
    t: (key: string) => string;
    onEdit?: (id: number) => void;
    onEditItem?: (object: T) => void;
    canEdit?: (object: T) => boolean;
    onActive?: (object: T) => void;
    canActive?: (object: T) => boolean;
    activeCondition?: (object: T) => boolean;
    onSurveys?: (object: T) => void;
    canSurveys?: (object: T) => boolean;
    onCopy?: (object: T) => void;
    canCopy?: (object: T) => boolean;
}

export function AppTableRowActions<T extends BaseListFields>({ object, onDelete, canDelete, onEnd, canEnd, onEdit, onEditItem ,canEdit, onView, canView, onRestore, canRestore, onActive, canActive, activeCondition, deleteCondition, onSurveys,canSurveys, onCopy, canCopy, t, sectionPermission }: AppTableRowActionsProps<T>) {
    const permissions = useUserPermissions();
    const canDeleteRow = canDelete ? canDelete(object) : true;
    const canEndRow = canEnd ? canEnd(object) : true;
    const canEditRow = canEdit ? canEdit(object) : true;
    const canViewRow = canView ? canView(object) : true;
    const canRestoreRow = canRestore ? canRestore(object) : true;
    const canActiveRow = canActive ? canActive(object) : true;
    const canSurveysRow = canSurveys ? canSurveys(object) : true;
    const canCopyRow = canCopy ? canCopy(object) : true;
    const endDateTime = object.endDate ? new Date(object.endDate).getTime() : Number.NaN;
    const shouldShowEndButton = !object.endDate || (Number.isFinite(endDateTime) && endDateTime > Date.now());
    const isInactive = activeCondition ? activeCondition(object) : isInactiveState(object.state, (object as any).deletedDate ?? (object as any).dataBaixa);
    const isActive = deleteCondition ? deleteCondition(object) : isActiveState(object.state, (object as any).deletedDate ?? (object as any).dataBaixa);

    return (
        <div className="d-inline-flex align-items-center justify-content-center gap-2">
            {onDelete && sectionPermission && permissions.includes(`${SectionPermissionTypeEnum[sectionPermission]}.DELETE`) &&
                canDeleteRow && isActive && (
                <Button
                    onClick={() => onDelete(object)}
                    className="btn btn-sm btn-gris2 btn-table-action"
                >
                    <i className="bi bi-x" title={t("common:buttons.buttonDelete")}></i>
                </Button>
            )}
            {shouldShowEndButton && onEnd && canEndRow && (
                <Button
                    onClick={() => onEnd(object)}
                    className="btn btn-sm btn-gris2 btn-table-action"
                >
                    <i className="bi bi-flag" title={t("common:buttons.buttonEnd")}></i>
                </Button>
            )}
            {onActive && canActiveRow && isInactive && (
                <Button
                    onClick={() => { onActive(object) }}
                    className="btn btn-sm btn-gris2 btn-table-action"
                >
                    <img src="/trash-can-undo-thin-full.svg" alt={t("common:buttons.buttonActive")} style={{ width: 16, height: 16 }} />
                </Button>
            )}
            {onRestore && canRestoreRow && isActive && (
                <Button
                    onClick={() => { onRestore(object) }}
                    className="btn btn-sm btn-gris2 btn-table-action"
                >
                    <i className="bi bi-key" title={t("common:buttons.buttonRestorePassword")}></i>
                </Button>
            )}
            {(onEdit || onEditItem) && sectionPermission && permissions.includes(`${SectionPermissionTypeEnum[sectionPermission]}.EDIT`) && canEditRow && (
                <Button
                    onClick={() => onEditItem ? onEditItem(object) : onEdit!(object.id)}
                    className="btn btn-sm btn-gris2 btn-table-action"
                >
                    <i className="bi bi-pen" title={t("common:buttons.buttonEdit")}></i>
                </Button>
            )}
            {onSurveys && canSurveysRow && (
                <Button
                    onClick={() => onSurveys(object)}
                    className="btn btn-sm btn-gris2 btn-table-action"
                >
                    <img
                        title={t("common:buttons.buttonSurveys")}
                        src={clipboardSurveysIcon}
                        alt={t("common:buttons.buttonSurveys")}
                        width={16}
                        height={16}
                    />
                </Button>
            )}
            {onCopy && canCopyRow && (
                <Button
                    onClick={() => onCopy(object)}
                    className="btn btn-sm btn-gris2 btn-table-action"
                >
                    <i className="bi bi-copy" title={t("common:buttons.buttonCopy")}></i>
                </Button>
            )}
            {onView && canViewRow && (                
                <Button
                    onClick={() => onView(object.id)}
                    className="btn btn-sm btn-gris2 btn-table-action"
                >
                    <i className="bi bi-file-text" title={t("common:buttons.buttonSeeDetail")}></i>
                </Button>
            )}
        </div>
    );
}
